import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SessionUser, UserRole } from '@/entities/session/types'
import {
  type AuthUser,
  type OnboardingPayload,
  authApi,
} from '@/shared/api/authApi'
import { supabase } from '@/shared/supabase/client'
import { useCalendarStore } from '@/features/calendar/model/calendarStore'
import { useClinicalDocumentStore } from '@/features/clinicalDocuments/model/clinicalDocumentStore'
import { useExamStore } from '@/features/exams/model/examStore'
import { usePatientStore } from '@/features/patients/model/patientStore'
import { shouldForceOnboardingCompleted } from '@/shared/lib/sessionUtils'
import { isNavigatorOnline } from '@/shared/lib/network'
import {
  clearSynapPersistedState,
  deletePersistenceUserData,
  localStorageJSON,
  setPersistenceUser,
} from '@/shared/lib/persistence'
import { DEFAULT_LOCAL_AI_MEMORY_RETENTION_DAYS } from '@/features/local-ai/localVectorStore'
import { clearUserDataStores } from '@/shared/lib/clearUserData'
import { clearLastLoginEmail } from '@/shared/lib/loginFormUtils'
import {
  clearStorageReencryptSessionMarkers,
  maybeReencryptLocalStorageOnLogin,
} from '@/shared/lib/storageReencryptOnLogin'
import { clearPwaCaches } from '@/shared/lib/clearPwaCaches'
import { Sentry } from '@/shared/lib/sentry'
import { logClientError } from '@/shared/lib/safeClientLog'
import { profilesRepository, type ProfileRecord } from '@/shared/repositories/profiles/profilesRepository'
import { quickActionsRepository } from '@/features/session/api/quickActionsRepository'
import {
  getScreenshotPersona,
  isScreenshotFixtureMode,
  shouldSkipScreenshotAutoSession,
} from '@/shared/lib/screenshotMode'
import { buildScreenshotFixtureSessionUser } from '@/shared/screenshotSeed/fixtureSessionUser'

/**
 * Reidrata do IndexedDB apenas quando offline. O app é focado em online; offline é fallback de urgência.
 * Quando online: não reidrata — o fetch da API é a fonte de verdade.
 */
const rehydrateAllDataStoresWhenOffline = async (): Promise<void> => {
  if (isNavigatorOnline()) return
  await Promise.all([
    usePatientStore.persist.rehydrate(),
    useExamStore.persist.rehydrate(),
    useCalendarStore.persist.rehydrate(),
    useClinicalDocumentStore.persist.rehydrate(),
  ])
}

const LOAD_INITIAL_DATA_TIMEOUT_MS = 45000

/** Fetches sequenciais com timeout e callback de status. Retorna lastQuickActionId e pinnedQuickActionId para uso imediato na página Início. */
const loadInitialDataWithStatus = async (
  onStatus?: (status: string) => void,
): Promise<{ lastQuickActionId: string | null; pinnedQuickActionId: string | null }> => {
  const { fetchPatients } = usePatientStore.getState()
  const { fetchExams } = useExamStore.getState()
  const { fetchEvents } = useCalendarStore.getState()
  const { fetchData: fetchDocs } = useClinicalDocumentStore.getState()

  const load = async (): Promise<{ lastQuickActionId: string | null; pinnedQuickActionId: string | null }> => {
    onStatus?.('Carregando pacientes...')
    await fetchPatients()
    onStatus?.('Carregando exames...')
    await fetchExams()
    onStatus?.('Carregando agenda...')
    await fetchEvents()
    onStatus?.('Carregando documentos...')
    await fetchDocs()
    const [lastId, pinnedId] = await Promise.all([
      quickActionsRepository.getLastQuickActionId().catch(() => null),
      quickActionsRepository.getPinnedQuickActionId().catch(() => null),
    ])
    return { lastQuickActionId: lastId, pinnedQuickActionId: pinnedId }
  }

  const timeout = new Promise<never>((_, reject) =>
    setTimeout(
      () => reject(new Error('O carregamento demorou muito. Verifique sua conexão e tente novamente.')),
      LOAD_INITIAL_DATA_TIMEOUT_MS,
    ),
  )

  return await Promise.race([load(), timeout])
}

const normalizeSpecialties = (value: unknown): string[] | undefined =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    : undefined

const clearPersistedClientState = async () => {
  await clearSynapPersistedState([
    'synap-session-store',
    'synap-patient-store',
    'synap-exam-store',
    'synap-calendar-store',
    'synap-clinical-document-store',
    'synap-offline-outbox',
    'synap-local-ai-store',
    'synap-notifications',
    'synap-chat-pending-requests',
    'synap-chat-focused-patient',
    'synap-chat-focused-patient-recents',
    'synap-chat-last-view',
    'synap-chat-outbound-queue',
  ])
}

/**
 * Limpa dados do usuário antes de carregar novo usuário.
 * Timeout obrigatório: IndexedDB `deleteDB` / open pode travar indefinidamente
 * (ex.: conexão reaberta por race open/close, ou IDB bloqueado) e deixava o login
 * preso em "Validando dados locais...".
 */
const CLEAR_DATA_BEFORE_LOGIN_TIMEOUT_MS = 8000

const clearDataBeforeUserChange = async () => {
  try {
    await Promise.race([
      clearUserDataStores(),
      new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error('clearDataBeforeUserChange timeout')),
          CLEAR_DATA_BEFORE_LOGIN_TIMEOUT_MS,
        ),
      ),
    ])
  } catch (err) {
    logClientError('[login] clearDataBeforeUserChange falhou ou expirou — seguindo com login', err)
  }
}

const applyProfileToSessionUser = (user: SessionUser, profile: ProfileRecord): SessionUser => {
  const specialties =
    profile.specialties ??
    normalizeSpecialties(user.specialties) ??
    normalizeSpecialties(user.especialidades)

  const phone1 = profile.phone1 ?? user.phone1 ?? user.telefone1
  const phone2 = profile.phone2 ?? user.phone2 ?? user.telefone2
  const isSystemAdmin = profile.isSystemAdmin
  const nextRole = profile.role
  const onboardingCompleted =
    user.onboardingCompleted || shouldForceOnboardingCompleted(nextRole, isSystemAdmin)

  return {
    ...user,
    name: profile.displayName || user.name,
    role: nextRole,
    crm: profile.crm ?? user.crm,
    specialties,
    especialidades: specialties,
    phone1,
    phone2,
    telefone1: phone1,
    telefone2: phone2,
    avatarUrl: profile.avatarUrl ?? user.avatarUrl,
    printLogoUrl: profile.printLogoUrl ?? user.printLogoUrl,
    printName: profile.printName ?? user.printName,
    contactEmail: profile.contactEmail ?? user.contactEmail,
    personType: profile.personType ?? user.personType,
    cpfCnpj: profile.cpfCnpj ?? user.cpfCnpj,
    pis: profile.pis ?? user.pis,
    tradeName: profile.tradeName ?? user.tradeName,
    isSystemAdmin,
    onboardingCompleted,
    onboardingWelcomeSeen: profile.onboardingWelcomeSeen || user.onboardingWelcomeSeen,
  }
}

const HYDRATE_PROFILE_TIMEOUT_MS = 15000

/** Evita chamadas concorrentes de hydrate para o mesmo usuário (cascade de profile fetches). */
let hydrateInFlight: Promise<SessionUser> | null = null
let hydrateUserId: string | null = null

const hydrateSessionUserWithProfile = async (user: SessionUser): Promise<SessionUser> => {
  if (hydrateInFlight && hydrateUserId === user.id) {
    return hydrateInFlight
  }

  const doHydrate = async (): Promise<SessionUser> => {
    const fetchProfile = async (): Promise<SessionUser> => {
    const { profile } = await profilesRepository.getProfileById(user.id, {
      id: user.id,
      email: user.email,
      name: user.name,
      role: user.role,
    })
      return applyProfileToSessionUser(user, profile)
    }

    const withTimeout = <T>(promise: Promise<T>, ms: number, label: string): Promise<T> =>
      Promise.race([
        promise,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error(`${label} demorou mais que ${ms / 1000}s. Verifique sua conexão.`)), ms),
        ),
      ])

    try {
      return await withTimeout(fetchProfile(), HYDRATE_PROFILE_TIMEOUT_MS, 'Carregar perfil')
    } catch (firstErr) {
      logClientError('[login] hydrateSessionUserWithProfile falhou (1ª tentativa)', firstErr)
      try {
        await new Promise((r) => setTimeout(r, 400))
        return await withTimeout(fetchProfile(), HYDRATE_PROFILE_TIMEOUT_MS, 'Carregar perfil')
      } catch (secondErr) {
        logClientError('[login] hydrateSessionUserWithProfile falhou (2ª tentativa)', secondErr)
        try {
          const isAdmin = await profilesRepository.getIsSystemAdminForUser(user.id)
          return { ...user, isSystemAdmin: isAdmin }
        } catch (thirdErr) {
          logClientError('[login] hydrateSessionUserWithProfile falhou (fallback isAdmin)', thirdErr)
          const msg =
            firstErr instanceof Error ? firstErr.message : 'Não foi possível carregar o perfil.'
          throw new Error(msg)
        }
      }
    }
  }

  hydrateUserId = user.id
  const p = doHydrate()
  hydrateInFlight = p
  p.finally(() => {
    if (hydrateInFlight === p) {
      hydrateInFlight = null
      hydrateUserId = null
    }
  })
  return p
}

const toSessionUser = (apiUser: AuthUser): SessionUser => {
  const onboardingCompleted =
    apiUser.onboardingCompleted ||
    shouldForceOnboardingCompleted(apiUser.role, apiUser.isSystemAdmin)

  return {
  id: apiUser.id,
  name: apiUser.name,
  email: apiUser.email,
  role: apiUser.role,
  crm: apiUser.crm,
  specialties: apiUser.specialties ?? apiUser.especialidades,
  phone1: apiUser.phone1 ?? apiUser.telefone1,
  phone2: apiUser.phone2 ?? apiUser.telefone2,
  printName: apiUser.printName,
  contactEmail: apiUser.contactEmail,
  personType: apiUser.personType,
  cpfCnpj: apiUser.cpfCnpj,
  pis: apiUser.pis,
  tradeName: apiUser.tradeName,
  especialidades: apiUser.especialidades,
  telefone1: apiUser.telefone1,
  telefone2: apiUser.telefone2,
  avatarUrl: apiUser.avatarUrl,
  isSystemAdmin: apiUser.isSystemAdmin,
  onboardingCompleted,
  onboardingWelcomeSeen: false,
  emailConfirmed: apiUser.emailConfirmed,
  }
}

interface SessionStoreState {
  user: SessionUser | null
  loading: boolean
  sessionRestored: boolean
  isLoggingOut: boolean
  /** Mensagem de progresso durante login; null quando não em login. */
  loginStatus: string | null
  /** Erro do último login; exibido no log; limpo ao iniciar novo login. */
  loginError: string | null
  /** ID do último atalho de ação rápida acessado; carregado no boot para exibir ordem correta na página Início. */
  lastQuickActionId: string | null
  /** ID do atalho fixado (pin); sempre exibido primeiro no carrossel. */
  pinnedQuickActionId: string | null

  loginWithCredentials: (email: string, password: string) => Promise<void>
  loginWithPasskey: () => Promise<void>
  completeOAuthLogin: (searchParams: URLSearchParams) => Promise<SessionUser>
  register: (name: string, email: string, password: string) => Promise<void>
  completeOnboarding: (data: OnboardingPayload) => Promise<void>
  confirmEmail: (token: string) => Promise<void>
  resendConfirmation: (email: string) => Promise<void>
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (token: string, password: string) => Promise<void>
  restoreSession: () => Promise<void>

  logout: () => Promise<void>
  refreshProfileFlags: (profile?: ProfileRecord) => Promise<void>
  updateProfile: (payload: {
    name?: string
    avatarUrl?: string
    printLogoUrl?: string
    crm?: string
    specialties?: string[]
    phone1?: string
    phone2?: string
    printName?: string
    contactEmail?: string
    personType?: 'pf' | 'pj'
    cpfCnpj?: string
    pis?: string
    tradeName?: string
  }) => Promise<void>
  changeRole: (role: UserRole) => Promise<void>
  setLastQuickActionId: (id: string) => void
  setPinnedQuickActionId: (id: string | null) => void
}

const syncSentryUser = (user: SessionUser | null) => {
  if (user) {
    /** Sem email/nome no Sentry: reduz PII em ambiente clínico; correlacionar por `id` (UUID Supabase). */
    Sentry.setUser({ id: user.id })
  } else {
    Sentry.setUser(null)
  }
}

let authStateListenerAttached = false

export const useSessionStore = create<SessionStoreState>()(
  persist(
    (set, get) => ({
      user: null,
      loading: false,
      sessionRestored: false,
      isLoggingOut: false,
      loginStatus: null,
      loginError: null,
      lastQuickActionId: null,
      pinnedQuickActionId: null,

      loginWithCredentials: async (email, password) => {
        set({ loading: true, loginStatus: 'Preparando dados locais...', loginError: null })
        try {
          await clearDataBeforeUserChange()
          set({ loginStatus: 'Autenticando...' })
          const { user } = await authApi.login(email, password)
          set({ loginStatus: 'Carregando perfil...' })
          const enrichedUser = await hydrateSessionUserWithProfile(toSessionUser(user))
          set({ user: enrichedUser, loginStatus: 'Preparando sessão local...' })
          syncSentryUser(enrichedUser)
          await maybeReencryptLocalStorageOnLogin(enrichedUser.id).catch((err) => {
            logClientError('[login] maybeReencryptLocalStorageOnLogin falhou — seguindo', err)
          })
          await rehydrateAllDataStoresWhenOffline()
          const { lastQuickActionId, pinnedQuickActionId } = await loadInitialDataWithStatus((status) => set({ loginStatus: status }))
          set({ sessionRestored: true, lastQuickActionId, pinnedQuickActionId })
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Não foi possível concluir o login.'
          set({ loginError: msg })
          logClientError('[login] loginWithCredentials falhou', err)
          throw err
        } finally {
          set({ loading: false, loginStatus: null })
        }
      },

      loginWithPasskey: async () => {
        set({ loading: true, loginStatus: 'Preparando dados locais...', loginError: null })
        try {
          await clearDataBeforeUserChange()
          set({ loginStatus: 'Autenticando com biometria ou chave de acesso...' })
          const { user } = await authApi.signInWithPasskey()
          set({ loginStatus: 'Carregando perfil...' })
          const enrichedUser = await hydrateSessionUserWithProfile(toSessionUser(user))
          set({ user: enrichedUser, loginStatus: 'Preparando sessão local...' })
          syncSentryUser(enrichedUser)
          await maybeReencryptLocalStorageOnLogin(enrichedUser.id).catch((err) => {
            logClientError('[login] maybeReencryptLocalStorageOnLogin falhou — seguindo', err)
          })
          await rehydrateAllDataStoresWhenOffline()
          const { lastQuickActionId, pinnedQuickActionId } = await loadInitialDataWithStatus((status) => set({ loginStatus: status }))
          set({ sessionRestored: true, lastQuickActionId, pinnedQuickActionId })
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Não foi possível concluir o login com chave de acesso.'
          set({ loginError: msg })
          logClientError('[login] loginWithPasskey falhou', err)
          throw err
        } finally {
          set({ loading: false, loginStatus: null })
        }
      },

      completeOAuthLogin: async (searchParams) => {
        set({ loading: true, loginStatus: 'Concluindo login com Google...', loginError: null })
        try {
          await clearDataBeforeUserChange()
          const { user } = await authApi.finishOAuthCallback(searchParams)
          set({ loginStatus: 'Carregando perfil...' })
          const enrichedUser = await hydrateSessionUserWithProfile(toSessionUser(user))
          set({ user: enrichedUser, loginStatus: 'Preparando sessão local...' })
          syncSentryUser(enrichedUser)
          await maybeReencryptLocalStorageOnLogin(enrichedUser.id).catch((err) => {
            logClientError('[login] maybeReencryptLocalStorageOnLogin falhou — seguindo', err)
          })
          await rehydrateAllDataStoresWhenOffline()
          const { lastQuickActionId, pinnedQuickActionId } = await loadInitialDataWithStatus((status) =>
            set({ loginStatus: status }),
          )
          set({ sessionRestored: true, lastQuickActionId, pinnedQuickActionId })
          return enrichedUser
        } catch (err) {
          const msg =
            err instanceof Error ? err.message : 'Não foi possível concluir o login com Google.'
          set({ loginError: msg })
          logClientError('[login] completeOAuthLogin falhou', err)
          throw err
        } finally {
          set({ loading: false, loginStatus: null })
        }
      },

      register: async (name, email, password) => {
        set({ loading: true })
        try {
          await clearDataBeforeUserChange()
          const { user } = await authApi.register(name, email, password)
          const enrichedUser = await hydrateSessionUserWithProfile(toSessionUser(user))
          set({ user: enrichedUser })
          syncSentryUser(enrichedUser)
          await maybeReencryptLocalStorageOnLogin(enrichedUser.id)
          await rehydrateAllDataStoresWhenOffline()
          const { lastQuickActionId, pinnedQuickActionId } = await loadInitialDataWithStatus()
          set({ sessionRestored: true, lastQuickActionId, pinnedQuickActionId })
        } finally {
          set({ loading: false })
        }
      },

      completeOnboarding: async (data) => {
        set({ loading: true })
        try {
          await clearDataBeforeUserChange()
          const { user } = await authApi.onboarding(data)
          const enrichedUser = await hydrateSessionUserWithProfile(toSessionUser(user))
          set({ user: enrichedUser })
          await maybeReencryptLocalStorageOnLogin(enrichedUser.id)
          await rehydrateAllDataStoresWhenOffline()
          const { lastQuickActionId, pinnedQuickActionId } = await loadInitialDataWithStatus()
          set({ sessionRestored: true, lastQuickActionId, pinnedQuickActionId })
        } finally {
          set({ loading: false })
        }
      },

      confirmEmail: async (token) => {
        await clearDataBeforeUserChange()
        const { user } = await authApi.confirmEmail(token)
        const enrichedUser = await hydrateSessionUserWithProfile(toSessionUser(user))
        set({ user: enrichedUser })
        await maybeReencryptLocalStorageOnLogin(enrichedUser.id)
        await rehydrateAllDataStoresWhenOffline()
        const { lastQuickActionId, pinnedQuickActionId } = await loadInitialDataWithStatus()
        set({ sessionRestored: true, lastQuickActionId, pinnedQuickActionId })
      },

      resendConfirmation: async (email) => {
        await authApi.resendConfirmation(email)
      },

      forgotPassword: async (email) => {
        await authApi.forgotPassword(email)
      },

      resetPassword: async (token, password) => {
        await clearDataBeforeUserChange()
        const { user } = await authApi.resetPassword(token, password)
        const enrichedUser = await hydrateSessionUserWithProfile(toSessionUser(user))
        set({ user: enrichedUser })
        await maybeReencryptLocalStorageOnLogin(enrichedUser.id)
        await rehydrateAllDataStoresWhenOffline()
        const { lastQuickActionId, pinnedQuickActionId } = await loadInitialDataWithStatus()
        set({ sessionRestored: true, lastQuickActionId, pinnedQuickActionId })
      },

      restoreSession: async () => {
        if (!authStateListenerAttached) {
          authApi.onAuthStateChange((user, event) => {
            void (async () => {
              if (!get().sessionRestored) return
              if (get().isLoggingOut) {
                if (!user) {
                  set({ user: null })
                  syncSentryUser(null)
                }
                return
              }

              if (!user) {
                return
              }

              const previousUserId = get().user?.id
              const isSameUser = previousUserId === user.id

              if (event === 'USER_UPDATED' && isSameUser) {
                return
              }

              if (event === 'TOKEN_REFRESHED' && isSameUser) {
                return
              }

              if (!isSameUser) {
                await clearDataBeforeUserChange()
              }

              const current = get().user
              const baseUser = toSessionUser(user)
              const userToHydrate =
                current?.id === user.id ? current : baseUser
              const enrichedUser = await hydrateSessionUserWithProfile(userToHydrate)
              set((state) =>
                state.user?.id === enrichedUser.id
                  ? { user: enrichedUser }
                  : state,
              )
            })()
          })
          authStateListenerAttached = true
        }

        if (isScreenshotFixtureMode()) {
          if (shouldSkipScreenshotAutoSession()) {
            set({ user: null, sessionRestored: true, loading: false })
            syncSentryUser(null)
            return
          }
          const persona = getScreenshotPersona()
          const user = buildScreenshotFixtureSessionUser(persona)
          set({
            user,
            sessionRestored: true,
            lastQuickActionId: 'quick-action-new-exam',
            pinnedQuickActionId: 'quick-action-new-document',
            loading: false,
          })
          syncSentryUser(user)
          return
        }

        set({ loading: true })
        try {
          /* refreshSession garante que o Supabase client tenha a sessão mais recente
           * antes de getMe e dos fetches. Evita usar sessão em cache desatualizada. */
          await supabase.auth.refreshSession().catch(() => {})
          const { user } = await authApi.getMe()
          const enrichedUser = await hydrateSessionUserWithProfile(toSessionUser(user))
          const previousUserId = get().user?.id
          if (previousUserId !== undefined && previousUserId !== enrichedUser.id) {
            await clearDataBeforeUserChange()
          }
          set({ user: enrichedUser })
          syncSentryUser(enrichedUser)
          await maybeReencryptLocalStorageOnLogin(enrichedUser.id)
          await rehydrateAllDataStoresWhenOffline()
          const { lastQuickActionId, pinnedQuickActionId } = await loadInitialDataWithStatus()
          set({ sessionRestored: true, lastQuickActionId, pinnedQuickActionId })
        } catch {
          set({ user: null, sessionRestored: true })
          syncSentryUser(null)
        } finally {
          set({ loading: false })
        }
      },

      refreshProfileFlags: async (profileArg) => {
        const current = get().user
        if (!current) return
        try {
          const profile = profileArg ?? (await profilesRepository.getCurrent()).profile
          set((state) => {
            if (!state.user || state.user.id !== current.id) return state
            return {
              user: {
                ...state.user,
                isSystemAdmin: profile.isSystemAdmin,
              },
            }
          })
        } catch {
          /* ignore */
        }
      },

      logout: async () => {
        set({ loading: true, isLoggingOut: true })
        try {
          await authApi.logout()
        } catch {
          // O estado local deve ser limpo mesmo quando o signOut remoto falha.
        } finally {
          try {
            const localAi = await import('@/features/local-ai/useLocalAIStore')
            await localAi.useLocalAIStore.getState().unloadModel()
            localAi.useLocalAIStore.setState({
              status: 'idle',
              downloadProgress: 0,
              downloadedBytes: 0,
              totalBytes: 0,
              error: null,
              selectedModelId: null,
              isLocalEnabled: false,
              isSyncEnabled: false,
              capability: null,
              engine: null,
              memoryRetentionDays: DEFAULT_LOCAL_AI_MEMORY_RETENTION_DAYS,
            })
          } catch {
            /* ignore */
          }
          /* T33: sem usuário ativo as gravações cifradas param; depois apaga chave e dados dele. */
          const loggedOutUserId = get().user?.id
          setPersistenceUser(null)
          await clearUserDataStores()
          if (loggedOutUserId) await deletePersistenceUserData(loggedOutUserId)
          await clearPersistedClientState()
          await clearPwaCaches()
          clearStorageReencryptSessionMarkers()
          clearLastLoginEmail()
          set({ user: null, loading: false, isLoggingOut: false })
          syncSentryUser(null)
        }
      },

      updateProfile: async (payload) => {
        const current = get().user
        if (!current) return

        const cleanName = payload.name?.trim()
        const cleanAvatarUrl = payload.avatarUrl?.trim() || undefined
        const cleanPrintLogoUrl = payload.printLogoUrl?.trim() || undefined
        const cleanCRM = payload.crm?.trim() || undefined
        const cleanPhone1 = payload.phone1?.trim() || undefined
        const cleanPhone2 = payload.phone2?.trim() || undefined
        const cleanPrintName = payload.printName?.trim() || undefined
        const cleanContactEmail = payload.contactEmail?.trim() || undefined
        const cleanCpfCnpj = payload.cpfCnpj?.trim() || undefined
        const cleanPis = payload.pis?.trim() || undefined
        const cleanTradeName = payload.tradeName?.trim() || undefined
        const cleanSpecialties = normalizeSpecialties(payload.specialties) ?? []

        set((state) => {
          if (!state.user) return state
          const nextName = cleanName
          const phone1 = cleanPhone1 ?? state.user.phone1 ?? state.user.telefone1
          const phone2 = cleanPhone2 ?? state.user.phone2 ?? state.user.telefone2
          return {
            user: {
              ...state.user,
              name: nextName || state.user.name,
              avatarUrl: cleanAvatarUrl ?? state.user.avatarUrl,
              printLogoUrl: cleanPrintLogoUrl ?? state.user.printLogoUrl,
              crm: cleanCRM ?? state.user.crm,
              specialties: cleanSpecialties.length ? cleanSpecialties : undefined,
              especialidades: cleanSpecialties.length ? cleanSpecialties : undefined,
              phone1,
              phone2,
              telefone1: phone1,
              telefone2: phone2,
              printName: cleanPrintName ?? state.user.printName,
              contactEmail: cleanContactEmail ?? state.user.contactEmail,
              personType: payload.personType ?? state.user.personType,
              cpfCnpj: cleanCpfCnpj ?? state.user.cpfCnpj,
              pis: cleanPis ?? state.user.pis,
              tradeName: cleanTradeName ?? state.user.tradeName,
            },
          }
        })

        try {
          const { profile } = await profilesRepository.updateCurrent({
            displayName: cleanName ?? null,
            avatarUrl: cleanAvatarUrl ?? null,
            printLogoUrl: cleanPrintLogoUrl ?? null,
            crm: cleanCRM ?? null,
            specialties: cleanSpecialties,
            phone1: cleanPhone1 ?? null,
            phone2: cleanPhone2 ?? null,
            printName: cleanPrintName ?? null,
            contactEmail: cleanContactEmail ?? null,
            personType: payload.personType ?? null,
            cpfCnpj: cleanCpfCnpj ?? null,
            pis: cleanPis ?? null,
            tradeName: cleanTradeName ?? null,
          })

          set((state) => {
            if (!state.user || state.user.id !== current.id) return state
            return {
              user: applyProfileToSessionUser(state.user, profile),
            }
          })

          void authApi.updateProfile({
            name: cleanName,
            crm: cleanCRM,
            specialties: cleanSpecialties,
            phone1: cleanPhone1,
            phone2: cleanPhone2,
            printName: cleanPrintName,
            contactEmail: cleanContactEmail,
            personType: payload.personType,
            cpfCnpj: cleanCpfCnpj,
            pis: cleanPis,
            tradeName: cleanTradeName,
          }).catch(() => {})
        } catch {
          set((state) => {
            if (!state.user || state.user.id !== current.id) return state
            return { user: current }
          })
        }
      },

      changeRole: async (role) => {
        const current = get().user
        if (!current) return

        const { user } = await authApi.updateProfile({ role })
        const enrichedUser = await hydrateSessionUserWithProfile(toSessionUser(user))
        set({ user: enrichedUser })
      },

      setLastQuickActionId: (id) => {
        set({ lastQuickActionId: id })
        void quickActionsRepository.updateLastQuickActionId(id).catch(() => {})
      },

      setPinnedQuickActionId: (id) => {
        set({ pinnedQuickActionId: id })
        void quickActionsRepository.updatePinnedQuickActionId(id).catch(() => {})
      },
    }),
    {
      name: 'synap-session-store',
      storage: localStorageJSON,
      /* Exclui avatarUrl e printLogoUrl da persistência: são data URLs (base64) que
       * podem exceder quota do localStorage e quebrar o app. São carregados via
       * hydrateSessionUserWithProfile ao restaurar sessão. */
      partialize: (state) => ({
        user: state.user
          ? {
              ...state.user,
              avatarUrl: undefined,
              printLogoUrl: undefined,
            }
          : null,
      }),
    },
  ),
)

/**
 * T33: o adaptador cifrado do IndexedDB usa a chave do usuário da sessão. Já vale com o
 * usuário reidratado do localStorage, para a fila offline poder ler no boot.
 */
let persistenceUserId: string | null = useSessionStore.getState().user?.id ?? null
setPersistenceUser(persistenceUserId)
useSessionStore.subscribe((state) => {
  const nextUserId = state.user?.id ?? null
  if (nextUserId === persistenceUserId || state.isLoggingOut) return
  persistenceUserId = nextUserId
  setPersistenceUser(nextUserId)
})
